"use client";

import { useEffect, useRef, useState } from "react";
import CustomButton from "rivals/components/shared/CustomButton";
import Link from "rivals/components/shared/Link";
import SocialMediaShareIcon from "rivals/components/shared/SocialMediaShareIcon";
import { SOCIAL_MEDIA } from "rivals/shared/constants";
import { useOnClickAway } from "rivals/shared/utils/hooks";
import { setData } from "rivals/shared/utils/qa";
import Facebook from "./Icons/Facebook";
import Printer from "./Icons/Printer";
import Reddit from "./Icons/Reddit";
import Share from "./Icons/Share";
import X from "./Icons/X";
import styles from "./Share.module.scss";

interface Props {
  data?: string;
}

const SHARE_MODULE_AUTOCLOSE_TIMEOUT = 5000;

function ShareModule({ data = "" }: Props): React.JSX.Element | null {
  const [location, setLocation] = useState<Location>();

  const [shareModuleOpen, setShareModuleOpen] = useState<boolean>(false);

  const [shareModuleTimeout, setShareModuleTimeout] = useState<
    NodeJS.Timeout | undefined
  >();

  const shareModuleRef = useRef(null);
  useOnClickAway(
    shareModuleRef,
    () => {
      if (shareModuleOpen) {
        setShareModuleOpen(false);
      }

      if (shareModuleTimeout) {
        clearTimeout(shareModuleTimeout);
        setShareModuleTimeout(undefined);
      }
    },
    [shareModuleRef, shareModuleOpen, shareModuleTimeout]
  );

  useEffect(() => {
    setLocation(window.location);
  }, []);

  useEffect(() => {
    // set timeout on open
    if (shareModuleOpen) {
      setShareModuleTimeout(
        setTimeout(() => {
          setShareModuleOpen(false);
        }, SHARE_MODULE_AUTOCLOSE_TIMEOUT)
      );
    }
    // clear timeout on close, but only if timeout exists
    else if (shareModuleTimeout) {
      clearTimeout(shareModuleTimeout);
      setShareModuleTimeout(undefined);
    }

    return () => {
      if (shareModuleTimeout) {
        clearTimeout(shareModuleTimeout);
      }
    };
  }, [shareModuleOpen, shareModuleTimeout]);

  const currentPageUrl = `${location?.origin}${location?.pathname}`;

  const icons = {
    [SOCIAL_MEDIA.TWITTER]: X,
    [SOCIAL_MEDIA.REDDIT]: Reddit,
    [SOCIAL_MEDIA.FACEBOOK]: Facebook
  };

  return (
    <div
      {...setData(data)}
      className={[
        styles.shareIconsContainer,
        shareModuleOpen ? styles.shadow : ""
      ].join(" ")}
      ref={shareModuleRef}
    >
      <span className={styles.shareLabel}>Share:</span>
      <div
        className={[
          styles.collapsibleShareButtonsContainer,
          shareModuleOpen ? "" : styles.collapsed
        ].join(" ")}
      >
        {[SOCIAL_MEDIA.TWITTER, SOCIAL_MEDIA.FACEBOOK, SOCIAL_MEDIA.REDDIT].map(
          socialMedia => {
            const Icon = icons[socialMedia];

            return (
              <SocialMediaShareIcon
                className={styles.shareOptionIcon}
                iconOverride={
                  <Icon
                    {...setData(
                      "social-media-share-icon",
                      socialMedia.toLowerCase()
                    )}
                  />
                }
                key={socialMedia}
                pageUrl={currentPageUrl}
                socialMedia={socialMedia}
                text="Read on Rivals"
              />
            );
          }
        )}
        <Link
          ariaLabel="Print page"
          className={styles.shareOptionIcon}
          href={`${currentPageUrl}/printable`}
          target="_blank"
        >
          <Printer
            {...setData("social-media-share-icon", "print")}
            style={{ height: "auto", width: "auto" }}
          />
        </Link>
      </div>
      <CustomButton
        ariaLabel="Open Share Menu"
        className={styles.shareOptionIcon}
        icon={<Share />}
        onClick={(): void => {
          setShareModuleOpen(!shareModuleOpen);
        }}
        philter="share"
      />
    </div>
  );
}

export default ShareModule;
